/**
 * Esta clase gestiona una lista de objetos que utilizan AsociableEstructura.
 * Estos objetos contienen toda la información de la estructura escolar:
 * {
     "anno_lectivo": {
         "id": 1029,
         "nombre": "2024-2025"
      },
      "ensennanza": {
          "id": 4728,
          "nombre": "Mecanizado",
          "etiqueta": "MEC"
      },
      ...
   }
 * Con los objetos en este formato, se pueden extraer listas únicas de cada nivel de
 * la estructura escolar (ensennanzas, niveles, cursos, modalidades, grupos) y
 * también construir un árbol jerárquico de la estructura escolar como el siguiente:
 * {
     id: anno_lectivo.id,
     nombre: anno_lectivo.nombre,
     ensennanzas: [
       {
         id: ensennanza.id,
         nombre: ensennanza.nombre,
         niveles: [
         ...
 * }
 */
export default class ListaObjetosConEstructura {
  static #CAMPOS_ESTRUCTURA = ['anno_lectivo', 'ensennanza', 'nivel', 'curso', 'modalidad', 'grupo'];

  constructor(arrayObjetos, _options = {}) {
    this.arrayObjetos = arrayObjetos;
  }

  #extraerElementosUnicos(propiedadPath) {
    const elementosSet = new Set();
    this.arrayObjetos.forEach((path) => {
      const elemento = path[propiedadPath];
      if (elemento) {
        elementosSet.add(elemento);
      }
    });
    return Array.from(elementosSet);
  }

  #filtrarPorPropiedad(propiedadPath, valorId) {
    return this.arrayObjetos.filter((path) => path[propiedadPath].id === valorId);
  }

  get ensennanzas() {
    return this.#extraerElementosUnicos('ensennanza');
  }

  get niveles() {
    return this.#extraerElementosUnicos('nivel');
  }

  get cursos() {
    return this.#extraerElementosUnicos('curso');
  }

  get modalidades() {
    return this.#extraerElementosUnicos('modalidad');
  }

  get grupos() {
    return this.#extraerElementosUnicos('grupo');
  }

  /**
   * Devuelve un árbol jerárquico de la estructura escolar basado en los objetos proporcionados:
   * {
   *   id: anno_lectivo.id,
   *   nombre: anno_lectivo.nombre,
   *   ensennanzas: [
   *     {
   *       id: ensennanza.id,
   *       nombre: ensennanza.nombre,
   *       niveles: [
   *         {
   *           id: nivel.id,
   *           nombre: nivel.nombre,
   *           cursos: [
   *             {
   *               id: curso.id,
   *               nombre: curso.nombre,
   *               modalidades: [
   *                 {
   *                   id: modalidad.id,
   *                   nombre: modalidad.nombre,
   *                   grupos: [
   *                     { id: grupo.id, nombre: grupo.nombre },
   *                     ...
   *                   ]
   */
  get arbolEstructuraEscolar() {
    const arbolMap = new Map();

    this.arrayObjetos.forEach((path) => {
      const {
        anno_lectivo: annoLectivo,
        ensennanza,
        nivel,
        curso,
        modalidad,
        grupo,
      } = path;

      // Determinar en qué nivel debe ir el grupo (el más específico disponible)
      const nivelGrupo = ListaObjetosConEstructura.#determinarNivelGrupo(grupo, {
        modalidad,
        curso,
        nivel,
        ensennanza,
      });

      // Nivel 1: Año lectivo (obligatorio)
      const annoLectivoNode = ListaObjetosConEstructura.#obtenerOCrearNodo(
        arbolMap,
        annoLectivo.id,
        annoLectivo,
        {
          ensennanzas: new Map(),
          grupos: new Map(),
        },
      );

      let nodoActual = annoLectivoNode;
      const nodos = { annoLectivo: annoLectivoNode };

      // Nivel 2: Enseñanza (opcional)
      if (ensennanza) {
        const ensennanzaNode = ListaObjetosConEstructura.#obtenerOCrearNodo(
          nodoActual.ensennanzas,
          ensennanza.id,
          ensennanza,
          {
            niveles: new Map(),
            grupos: new Map(),
          },
        );
        nodoActual = ensennanzaNode;
        nodos.ensennanza = ensennanzaNode;

        // Nivel 3: Nivel (opcional)
        if (nivel) {
          const nivelNode = ListaObjetosConEstructura.#obtenerOCrearNodo(
            ensennanzaNode.niveles,
            nivel.id,
            nivel,
            {
              cursos: new Map(),
              grupos: new Map(),
            },
          );
          nodoActual = nivelNode;
          nodos.nivel = nivelNode;

          // Nivel 4: Curso (opcional)
          if (curso) {
            const cursoNode = ListaObjetosConEstructura.#obtenerOCrearNodo(
              nivelNode.cursos,
              curso.id,
              curso,
              {
                modalidades: new Map(),
                grupos: new Map(),
              },
            );
            nodoActual = cursoNode;
            nodos.curso = cursoNode;

            // Nivel 5: Modalidad (opcional)
            if (modalidad) {
              const modalidadNode = ListaObjetosConEstructura.#obtenerOCrearNodo(
                cursoNode.modalidades,
                modalidad.id,
                modalidad,
                {
                  grupos: new Map(),
                },
              );
              nodoActual = modalidadNode;
              nodos.modalidad = modalidadNode;
            }
          }
        }
      }

      // Añadir grupo solo en el nivel correspondiente
      ListaObjetosConEstructura.#añadirGrupoEnNivel(grupo, nivelGrupo, nodos);
    });

    return Array.from(arbolMap.values()).map(ListaObjetosConEstructura.#convertirMapsAArrays);
  }

  deEnsennanza(ensennanzaId) {
    return this.#filtrarPorPropiedad('ensennanza', ensennanzaId);
  }

  deNivel(nivelId) {
    return this.#filtrarPorPropiedad('nivel', nivelId);
  }

  deCurso(cursoId) {
    return this.#filtrarPorPropiedad('curso', cursoId);
  }

  deModalidad(modalidadId) {
    return this.#filtrarPorPropiedad('modalidad', modalidadId);
  }

  deGrupo(grupoId) {
    return this.#filtrarPorPropiedad('grupo', grupoId);
  }

  static #determinarNivelGrupo(grupo, {
    modalidad,
    curso,
    nivel,
    ensennanza,
  }) {
    if (!grupo) return null;

    if (modalidad) return 'modalidad';
    if (curso) return 'curso';
    if (nivel) return 'nivel';
    if (ensennanza) return 'ensennanza';
    return null;
  }

  static #obtenerOCrearNodo(mapa, id, objetoCompleto, propiedadesHijas = {}) {
    if (!mapa.has(id)) {
      // Copiar todo el objeto y eliminar los campos de estructura escolar
      const nodoDefault = { ...objetoCompleto };

      // Eliminar los campos de estructura
      ListaObjetosConEstructura.#CAMPOS_ESTRUCTURA.forEach((campo) => {
        delete nodoDefault[campo];
      });

      // Añadir las propiedades hijas (ensennanzas, niveles, etc.)
      Object.assign(nodoDefault, propiedadesHijas);

      mapa.set(id, nodoDefault);
    }
    return mapa.get(id);
  }

  static #añadirGrupoEnNivel(grupo, nivelGrupo, nodos) {
    if (!grupo || !nivelGrupo) return;

    const nodoDestino = nodos[nivelGrupo];
    if (nodoDestino && !nodoDestino.grupos.has(grupo.id)) {
      nodoDestino.grupos.set(grupo.id, {
        id: grupo.id,
        nombre: grupo.nombre,
        etiqueta: grupo.etiqueta,
      });
    }
  }

  static #convertirMapsAArrays(nodo) {
    if (nodo.ensennanzas) {
      nodo.ensennanzas = Array.from(nodo.ensennanzas.values())
        .map(ListaObjetosConEstructura.#convertirMapsAArrays)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    if (nodo.niveles) {
      nodo.niveles = Array.from(nodo.niveles.values())
        .map(ListaObjetosConEstructura.#convertirMapsAArrays)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    if (nodo.cursos) {
      nodo.cursos = Array.from(nodo.cursos.values())
        .map(ListaObjetosConEstructura.#convertirMapsAArrays)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    if (nodo.modalidades) {
      nodo.modalidades = Array.from(nodo.modalidades.values())
        .map(ListaObjetosConEstructura.#convertirMapsAArrays)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    if (nodo.grupos) {
      nodo.grupos = Array.from(nodo.grupos.values())
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    }
    return nodo;
  }
}
